import { useDropdownContext } from "@circle-react-uikit/Dropdown";
import type { PathWithState } from "@circle-react/helpers/urlHelpers/memberProfileModalPaths";
import { Link, type HistoryState } from "@tanstack/react-router";
import isFunction from "lodash/isFunction";
import noop from "lodash/noop";
import type {
  ComponentPropsWithoutRef,
  ForwardedRef,
  MouseEventHandler,
  ReactNode,
} from "react";
import { forwardRef } from "react";

import { classNames } from "@/react/helpers/twMergeWithCN";

type LinkRouterProps = ComponentPropsWithoutRef<typeof Link>;

export type ItemLinkTo = string | PathWithState;

interface CommonProps {
  active?: boolean;
  children?: ReactNode;
  forceClose?: boolean;
  hasDanger?: boolean;
  isDark?: boolean;
  isDisabled?: boolean;
}

interface LinkProps
  extends CommonProps, Omit<LinkRouterProps, "children" | "to"> {
  to: ItemLinkTo;
}

interface ButtonProps extends CommonProps, ComponentPropsWithoutRef<"button"> {
  linkType?: ComponentPropsWithoutRef<"button">["type"];
}

export type ItemLinkProps = LinkProps | ButtonProps;
export type ItemLinkElement = HTMLAnchorElement | HTMLButtonElement;

const isLinkProps = (props: ItemLinkProps): props is LinkProps =>
  "to" in props && Boolean(props.to);

const isButtonProps = (props: ItemLinkProps): props is ButtonProps =>
  !isLinkProps(props);

const resolveLinkTarget = (to: ItemLinkTo) => {
  if (typeof to !== "string") {
    return {
      to: to.pathname,
      state: to.state,
    };
  }

  return { to };
};

export const ItemLink = forwardRef<ItemLinkElement, ItemLinkProps>(
  (
    {
      active,
      children,
      forceClose = false,
      hasDanger = false,
      onClick = noop,
      isDark = false,
      isDisabled = false,
      className,
      ...rest
    },
    ref,
  ) => {
    const { isOpen, toggle } = useDropdownContext();

    const handleClick: MouseEventHandler<ItemLinkElement> = event => {
      if (forceClose && isOpen) {
        toggle();
      }

      if (isFunction(onClick)) {
        onClick(event);
      }
    };

    const linkClassNames = classNames(
      "block w-full text-left group py-[0.438rem] px-4 rounded-md hover:bg-tertiary !text-sm font-medium transition-colors duration-75",
      {
        "opacity-30": isDisabled,
        "hover:text-dark hover:bg-secondary": !isDark,
        "text-default": !hasDanger && !isDark,
        "bg-tertiary": active && !isDark,
        "text-v2-danger hover:text-v2-danger": hasDanger,
        "text-white hover:bg-tertiary": isDark,
      },
      className,
    );

    if (isLinkProps(rest)) {
      const { to, ...linkRest } = rest;
      const { to: pathname, state } = resolveLinkTarget(to);

      return (
        <Link
          ref={ref as ForwardedRef<HTMLAnchorElement>}
          className={linkClassNames}
          onClick={handleClick}
          {...linkRest}
          to={pathname}
          state={state as HistoryState}
        >
          {children}
        </Link>
      );
    }

    if (isButtonProps(rest)) {
      const { linkType = "submit", ...restButton } = rest;
      return (
        <button
          ref={ref as ForwardedRef<HTMLButtonElement>}
          onClick={handleClick}
          className={linkClassNames}
          {...restButton}
          // eslint-disable-next-line react/button-has-type -- Backwards compatibility with legacy component API
          type={linkType}
          disabled={isDisabled}
        >
          {children}
        </button>
      );
    }

    return null;
  },
);

ItemLink.displayName = "ItemLink";
